import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import CustomFieldsPanel from "@/components/app/CustomFieldsPanel";
import { Plus, Users, Mail, Phone, Building2, Trash2, Link2, X, Search, MessageCircle, MessageSquare, Video, Hash, Slack, Pencil, Check } from "lucide-react";
import { toast } from "sonner";

const CONTACT_TYPES = [
  { key: "stakeholder", label: "Stakeholder", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { key: "sponsor", label: "Sponsor", color: "bg-purple-50 text-purple-700 border-purple-200" },
  { key: "client", label: "Client", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "vendor", label: "Vendor", color: "bg-amber-50 text-amber-800 border-amber-200" },
  { key: "team", label: "Team", color: "bg-[#111827] text-slate-300 border-[#1F2937]" },
  { key: "other", label: "Other", color: "bg-[#030712] text-slate-400 border-[#1F2937]" },
];

const COMM_CHANNELS = [
  { key: "email", label: "Email", icon: Mail },
  { key: "phone", label: "Phone", icon: Phone },
  { key: "slack", label: "Slack", icon: Slack },
  { key: "teams", label: "MS Teams", icon: MessageSquare },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { key: "video", label: "Video call", icon: Video },
  { key: "in_person", label: "In person", icon: Users },
  { key: "other", label: "Other", icon: Hash },
];
const commMeta = (k) => COMM_CHANNELS.find((c) => c.key === k) || COMM_CHANNELS[COMM_CHANNELS.length - 1];

const LEVELS = ["low", "medium", "high"];
const RACI_ROLES = [
  { key: "responsible", label: "Responsible (R)", color: "bg-blue-50 text-blue-700 border-blue-300" },
  { key: "accountable", label: "Accountable (A)", color: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  { key: "consulted", label: "Consulted (C)", color: "bg-purple-50 text-purple-700 border-purple-300" },
  { key: "informed", label: "Informed (I)", color: "bg-[#111827] text-slate-300 border-[#374151]" },
];

const typeMeta = (k) => CONTACT_TYPES.find((t) => t.key === k) || CONTACT_TYPES[CONTACT_TYPES.length - 1];

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("__all__");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", job_title: "",
    contact_type: "stakeholder", influence: "medium", interest: "medium",
    preferred_communication: "email", communication_cadence: "",
    tags: "", notes: "",
  });

  // Selected contact drawer
  const [selected, setSelected] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkForm, setLinkForm] = useState({ project_id: "", raci_role: "informed" });

  const load = useCallback(async () => {
    const [c, p] = await Promise.all([
      api.get("/contacts"),
      api.get("/projects"),
    ]);
    setContacts(c.data);
    setProjects(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const createContact = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    const payload = {
      ...form,
      email: form.email || null,
      tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
    };
    try {
      await api.post("/contacts", payload);
      setOpen(false);
      setForm({ name: "", email: "", phone: "", company: "", job_title: "", contact_type: "stakeholder", influence: "medium", interest: "medium", preferred_communication: "email", communication_cadence: "", tags: "", notes: "" });
      toast.success("Contact added");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const deleteContact = async (cid) => {
    if (!window.confirm("Delete this contact and all project links?")) return;
    await api.delete(`/contacts/${cid}`);
    if (selected?.contact_id === cid) setSelected(null);
    toast.success("Contact removed");
    load();
  };

  const openDetail = async (cid) => {
    const { data } = await api.get(`/contacts/${cid}`);
    setSelected(data);
  };

  const linkProject = async () => {
    if (!linkForm.project_id) return;
    await api.post(`/contacts/${selected.contact_id}/project-links`, linkForm);
    setLinkOpen(false);
    setLinkForm({ project_id: "", raci_role: "informed" });
    toast.success("Linked to project");
    openDetail(selected.contact_id);
    load();
  };

  const unlinkProject = async (pid) => {
    await api.delete(`/contacts/${selected.contact_id}/project-links/${pid}`);
    openDetail(selected.contact_id);
    load();
  };

  const filtered = contacts
    .filter((c) => filter === "__all__" || c.contact_type === filter)
    .filter((c) => {
      const q = search.toLowerCase().trim();
      if (!q) return true;
      return [c.name, c.email, c.company, c.job_title].filter(Boolean).join(" ").toLowerCase().includes(q);
    });

  const counts = CONTACT_TYPES.reduce((acc, t) => {
    acc[t.key] = contacts.filter((c) => c.contact_type === t.key).length;
    return acc;
  }, {});

  return (
    <div>
      <PageHeader
        eyebrow="Stakeholder & Project CRM"
        title="Communication Charter"
        description="Single source of truth for stakeholders, sponsors, clients, and vendors — with preferred channel, cadence, and RACI mapped to projects."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="new-contact-btn">
                <Plus className="w-4 h-4 mr-1" /> New contact
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-gray-900 sm:max-w-xl">
              <DialogHeader><DialogTitle className="font-display tracking-tight">New contact</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Full name</Label>
                    <Input className="rounded-none mt-1" data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Job title</Label>
                    <Input className="rounded-none mt-1" data-testid="contact-title-input" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Email</Label>
                    <Input type="email" className="rounded-none mt-1" data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Phone</Label>
                    <Input className="rounded-none mt-1" data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Company</Label>
                    <Input className="rounded-none mt-1" data-testid="contact-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Type</Label>
                    <Select value={form.contact_type} onValueChange={(v) => setForm({ ...form, contact_type: v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="contact-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{CONTACT_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Influence</Label>
                    <Select value={form.influence} onValueChange={(v) => setForm({ ...form, influence: v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="contact-influence-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l} className="capitalize">{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Interest</Label>
                    <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="contact-interest-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l} className="capitalize">{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Preferred channel</Label>
                    <Select value={form.preferred_communication} onValueChange={(v) => setForm({ ...form, preferred_communication: v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="contact-comm-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{COMM_CHANNELS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Cadence</Label>
                    <Input className="rounded-none mt-1" placeholder="e.g. Weekly, Bi-weekly" data-testid="contact-cadence-input" value={form.communication_cadence} onChange={(e) => setForm({ ...form, communication_cadence: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.2em]">Tags (comma separated)</Label>
                  <Input className="rounded-none mt-1" placeholder="board, finance, exec" data-testid="contact-tags-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.2em]">Notes</Label>
                  <Textarea className="rounded-none mt-1" data-testid="contact-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={createContact} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="confirm-create-contact">Add contact</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="px-5 sm:px-8 lg:px-12 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-px bg-[#1F2937] border border-[#1F2937] min-h-[60vh]">
          {/* List */}
          <div className="bg-[#0D1117]">
            <div className="px-6 py-3 border-b border-[#1F2937] flex flex-wrap items-center gap-3">
              <Users className="w-4 h-4 text-gray-500" />
              <div className="font-display font-bold tracking-tight text-sm">Charter <span className="text-gray-400 font-normal">({contacts.length})</span></div>
              <div className="ml-auto flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-2.5" />
                  <Input className="rounded-none pl-8 h-8 w-56" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="contact-search-input" />
                </div>
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="rounded-none w-40 h-8 text-xs" data-testid="contact-type-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All types</SelectItem>
                    {CONTACT_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label} ({counts[t.key] || 0})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="p-10 text-center text-sm text-gray-500" data-testid="empty-contacts">No contacts {search ? "matching your search" : "yet"}.</div>
            ) : (
              <div className="divide-y divide-[#1F2937]">
                {filtered.map((c) => {
                  const meta = typeMeta(c.contact_type);
                  const ch = commMeta(c.preferred_communication || "email");
                  const ChIcon = ch.icon;
                  return (
                    <button
                      key={c.contact_id}
                      onClick={() => openDetail(c.contact_id)}
                      className={`w-full text-left px-6 py-4 hover:bg-[#030712] flex items-start gap-3 ${selected?.contact_id === c.contact_id ? "bg-[#030712]" : ""}`}
                      data-testid={`contact-row-${c.contact_id}`}
                    >
                      <div className="w-9 h-9 rounded-full bg-gray-900 text-white grid place-items-center text-xs font-semibold shrink-0">{c.name[0]?.toUpperCase()}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-medium text-sm truncate">{c.name}</div>
                          <span className={`text-[10px] uppercase tracking-[0.15em] border px-1.5 py-0.5 ${meta.color}`}>{meta.label}</span>
                        </div>
                        <div className="text-xs text-gray-500 truncate">{[c.job_title, c.company].filter(Boolean).join(" · ")}</div>
                        <div className="text-[11px] text-gray-400 font-mono mt-1 flex items-center gap-3 flex-wrap">
                          <span className="flex items-center gap-1 text-slate-300" data-testid={`row-channel-${c.contact_id}`}>
                            <ChIcon className="w-3 h-3" /> {ch.label}
                            {c.communication_cadence && <span className="text-gray-400">· {c.communication_cadence}</span>}
                          </span>
                          {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>}
                          {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                        </div>
                      </div>
                      <div className="text-[10px] text-gray-400 uppercase tracking-[0.2em] text-right shrink-0">
                        <div>Inf: {c.influence}</div>
                        <div>Int: {c.interest}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Detail */}
          <div className="bg-[#0D1117]">
            {!selected ? (
              <div className="p-10 text-center text-sm text-gray-500" data-testid="contact-detail-empty">
                <Users className="w-8 h-8 mx-auto mb-3 text-gray-300" />
                Select a contact to see project links and RACI mapping.
              </div>
            ) : (
              <div data-testid="contact-detail-panel">
                <div className="px-6 py-4 border-b border-[#1F2937] flex items-start justify-between">
                  <div>
                    <div className="font-display font-bold tracking-tight text-lg" data-testid="detail-name">{selected.name}</div>
                    <div className="text-xs text-gray-500 mt-1">{[selected.job_title, selected.company].filter(Boolean).join(" · ")}</div>
                  </div>
                  <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-white" data-testid="close-detail">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-6 space-y-4 text-sm">
                  {/* Communication charter highlight (edit-in-place) */}
                  <CommCharterBlock
                    contact={selected}
                    onSave={async (patch) => {
                      await api.patch(`/contacts/${selected.contact_id}`, patch);
                      toast.success("Charter updated");
                      const { data } = await api.get(`/contacts/${selected.contact_id}`);
                      setSelected(data);
                      load();
                    }}
                  />
                  {selected.email && <div className="flex items-center gap-2 text-slate-300"><Mail className="w-3.5 h-3.5 text-gray-400" /> {selected.email}</div>}
                  {selected.phone && <div className="flex items-center gap-2 text-slate-300"><Phone className="w-3.5 h-3.5 text-gray-400" /> {selected.phone}</div>}
                  {selected.company && <div className="flex items-center gap-2 text-slate-300"><Building2 className="w-3.5 h-3.5 text-gray-400" /> {selected.company}</div>}

                  {selected.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {selected.tags.map((t) => <span key={t} className="text-[10px] bg-[#111827] px-1.5 py-0.5">{t}</span>)}
                    </div>
                  )}

                  {selected.notes && <div className="text-xs bg-[#030712] p-3 border border-[#1F2937] whitespace-pre-wrap">{selected.notes}</div>}

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2"><Link2 className="w-3 h-3" /> Project links (RACI)</div>
                      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="rounded-none h-7 text-xs" data-testid="link-project-btn">
                            <Plus className="w-3 h-3 mr-1" /> Link
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-none border-gray-900 sm:max-w-md">
                          <DialogHeader><DialogTitle className="font-display tracking-tight">Link to project</DialogTitle></DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label className="text-[11px] uppercase tracking-[0.2em]">Project</Label>
                              <Select value={linkForm.project_id} onValueChange={(v) => setLinkForm({ ...linkForm, project_id: v })}>
                                <SelectTrigger className="rounded-none mt-1" data-testid="link-project-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                                <SelectContent>{projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-[11px] uppercase tracking-[0.2em]">RACI role</Label>
                              <Select value={linkForm.raci_role} onValueChange={(v) => setLinkForm({ ...linkForm, raci_role: v })}>
                                <SelectTrigger className="rounded-none mt-1" data-testid="link-raci-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{RACI_ROLES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                          </div>
                          <DialogFooter>
                            <Button onClick={linkProject} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="confirm-link-project">Link</Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>

                    {selected.project_links?.length === 0 && <div className="text-xs text-gray-400 italic">Not linked to any project.</div>}
                    <div className="space-y-1.5">
                      {selected.project_links?.map((l) => {
                        const r = RACI_ROLES.find((rr) => rr.key === l.raci_role) || RACI_ROLES[3];
                        return (
                          <div key={l.link_id || l.project_id} className="flex items-center gap-2 p-2 border border-[#1F2937] hover:bg-[#030712]" data-testid={`project-link-${l.project_id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm truncate">{l.name || l.project_id}</div>
                              <div className="text-[10px] text-gray-500 capitalize">{l.status?.replace("_", " ")}</div>
                            </div>
                            <span className={`text-[10px] uppercase tracking-[0.15em] border px-1.5 py-0.5 ${r.color}`}>{r.key.charAt(0).toUpperCase()}</span>
                            <button onClick={() => unlinkProject(l.project_id)} className="text-gray-300 hover:text-rose-600" data-testid={`unlink-${l.project_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <CustomFieldsPanel entityType="contact" entityId={selected.contact_id} />

                  <div className="pt-4 border-t border-[#1F2937]">
                    <button onClick={() => deleteContact(selected.contact_id)} className="text-xs text-rose-600 hover:underline" data-testid="delete-contact-btn">
                      Delete contact
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ----- Inline-editable Communication Charter block -----
const CommCharterBlock = ({ contact, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [channel, setChannel] = useState(contact.preferred_communication || "email");
  const [cadence, setCadence] = useState(contact.communication_cadence || "");

  useEffect(() => {
    setChannel(contact.preferred_communication || "email");
    setCadence(contact.communication_cadence || "");
    setEditing(false);
  }, [contact.contact_id, contact.preferred_communication, contact.communication_cadence]);

  const ch = commMeta(channel);
  const ChIcon = ch.icon;

  const save = async () => {
    await onSave({ preferred_communication: channel, communication_cadence: cadence });
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="border border-gray-900 p-3 flex items-center gap-3 bg-[#030712]" data-testid="detail-comm-charter">
        <ChIcon className="w-5 h-5 text-[#002FA7] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Preferred communication</div>
          <div className="text-sm font-semibold">
            {ch.label}
            {contact.communication_cadence && <span className="text-gray-500 font-normal ml-2">· {contact.communication_cadence}</span>}
          </div>
        </div>
        <button
          onClick={() => setEditing(true)}
          className="text-gray-400 hover:text-white p-1"
          aria-label="Edit communication preferences"
          data-testid="edit-comm-charter-btn"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="border border-gray-900 p-3 bg-[#030712] space-y-2" data-testid="detail-comm-charter-edit">
      <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Preferred communication</div>
      <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center">
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger className="rounded-none h-8 text-xs" data-testid="edit-comm-channel-select"><SelectValue /></SelectTrigger>
          <SelectContent>{COMM_CHANNELS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
        <Input
          value={cadence}
          onChange={(e) => setCadence(e.target.value)}
          placeholder="Cadence"
          className="rounded-none h-8 text-xs"
          data-testid="edit-comm-cadence-input"
        />
        <button onClick={save} className="text-emerald-700 hover:text-emerald-900 p-1.5 border border-emerald-700" data-testid="save-comm-charter-btn" aria-label="Save">
          <Check className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => setEditing(false)} className="text-gray-500 hover:text-white p-1.5 border border-[#374151]" data-testid="cancel-comm-charter-btn" aria-label="Cancel">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default Contacts;
